/**
 * An aircraft's own photo (MB's decision 598, Q523 B, 26 Oct 2026; docs/specs/cloud/aircraft-photo.md).
 *
 * Tenant admins, managers and office staff (decision 664) set it in Teslyn Cloud's Aircraft register, from a project
 * photo already in Teslyn
 * ("Use as aircraft photo" on the photo, "Choose from photos" on the aircraft) or by uploading one. Teslyn Cloud
 * keeps it as a JPEG of the display size (2048 px on the long edge) in the private bucket `aircraft-photos`, at
 * `tenants/<tenant>/aircraft/<aircraft>/<file id>.jpg`, and `aircraft.cloud_photo_path` names that object (the
 * older `photo_path` is a file on one laptop, which no screen sets; an older Field ignores the new column). A new photo
 * gets a new file id, so a laptop sees the change and never shows an old copy. The aircraft row syncs to Teslyn
 * Field (`tenant_aircraft`), which keeps a copy in the project folder (`Teslyn/aircraft-photos/…`) for the daily
 * report's aircraft section and the weekly report's cover; without a copy (offline, signed out) the reports use the
 * aircraft-type photo as before.
 *
 * The database decides who may set it (`aircraft_photo_guard` and the bucket's policies,
 * `20261026000200_aircraft_photo_from_photos.sql`); these rules only decide what the apps show.
 */
import type { PhotoCategory } from './photos.js';

export const AIRCRAFT_PHOTO_BUCKET = 'aircraft-photos';

/** The bucket's limit: a display-size JPEG is far smaller (about 0.2 to 2 MB). */
export const AIRCRAFT_PHOTO_MAX_BYTES = 10 * 1024 * 1024;

/**
 * Who sets an aircraft's photo: tenant admins and managers (decision 598) and office staff (decision 664, Q589 B).
 * Mirrored by public.is_aircraft_photo_setter (20261026001010_aircraft_photo_office_staff.sql).
 */
export const AIRCRAFT_PHOTO_SETTERS = ['tenant_admin', 'manager', 'office_staff'] as const;

export function canSetAircraftPhoto(roles: readonly string[] | null): boolean {
  return (roles ?? []).some((r) => (AIRCRAFT_PHOTO_SETTERS as readonly string[]).includes(r));
}

const UUID = '[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}';
const OBJECT = new RegExp(`^tenants/(${UUID})/aircraft/(${UUID})/(${UUID})\\.jpg$`);

/** `tenants/<tenant>/aircraft/<aircraft>/<file id>.jpg` (lower-case ids, as the database's pattern). */
export function aircraftPhotoObjectPath(tenantId: string, aircraftId: string, fileId: string): string {
  const path = `tenants/${tenantId.toLowerCase()}/aircraft/${aircraftId.toLowerCase()}/${fileId.toLowerCase()}.jpg`;
  if (!OBJECT.test(path)) throw new Error('An aircraft photo is named by the tenant, aircraft and file ids.');
  return path;
}

export interface AircraftPhotoObject {
  readonly tenantId: string;
  readonly aircraftId: string;
  readonly fileId: string;
}

/** The parts of an aircraft photo set in Teslyn Cloud, or null for anything else (a laptop path, empty). */
export function aircraftPhotoObjectParts(path: string | null | undefined): AircraftPhotoObject | null {
  const m = OBJECT.exec(path ?? '');
  return m ? { tenantId: m[1]!, aircraftId: m[2]!, fileId: m[3]! } : null;
}

/** Whether `path` (an `aircraft.cloud_photo_path`) names an aircraft photo in Teslyn Cloud. */
export const isCloudAircraftPhoto = (path: string | null | undefined): boolean =>
  aircraftPhotoObjectParts(path) !== null;

/**
 * Where Teslyn Field keeps its copy, inside the project folder (`/`-separated, relative to the folder, as the
 * report core resolves a file): `Teslyn/aircraft-photos/<aircraft>/<file id>.jpg`. Null for a path that is not a
 * Teslyn Cloud aircraft photo. The Field core checks the same pattern before it writes.
 */
export function aircraftPhotoLocalPath(path: string | null | undefined): string | null {
  const p = aircraftPhotoObjectParts(path);
  return p ? `Teslyn/aircraft-photos/${p.aircraftId}/${p.fileId}.jpg` : null;
}

/** A project photo as the "Choose from photos" list needs it. */
export interface AircraftPhotoCandidate {
  readonly id: string;
  readonly category: PhotoCategory | string;
  readonly caption: string;
  readonly file_name: string;
  readonly created_at: string;
}

/**
 * The project photos to choose an aircraft's photo from, best first: photos whose caption or file name names the
 * registration (with or without the dash: "VH-JOZ", "JOZ"), then the Aircraft category, then the rest; newest
 * first within each.
 */
export function aircraftPhotoCandidates<T extends AircraftPhotoCandidate>(
  photos: readonly T[],
  registration: string,
): T[] {
  const reg = registration.trim().toUpperCase();
  const tail = reg.includes('-') ? reg.slice(reg.indexOf('-') + 1) : '';
  const names = (p: T): boolean => {
    const text = `${p.caption} ${p.file_name}`.toUpperCase();
    if (reg !== '' && (text.includes(reg) || text.includes(reg.replace('-', '')))) return true;
    return tail.length >= 3 && new RegExp(`(^|[^A-Z0-9])${tail}([^A-Z0-9]|$)`).test(text);
  };
  const rank = (p: T): number => (names(p) ? 0 : p.category === 'aircraft' ? 1 : 2);
  return [...photos].sort((a, b) => rank(a) - rank(b) || b.created_at.localeCompare(a.created_at));
}

/** The words for an aircraft's photo on the register: its own (set in Teslyn Cloud) or the aircraft-type photo. */
export function aircraftPhotoWords(a: { readonly cloud_photo_path?: string | null }): string {
  return isCloudAircraftPhoto(a.cloud_photo_path) ? 'Own photo' : 'Aircraft-type photo';
}
